import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BrandLogo } from '@/components/brand/BrandLogo';
import { Button } from '@/components/ui/Button';
import { GoldenRule } from '@/components/ui/GoldenRule';
import { supabase } from '@/lib/supabase';
import { sanitizeInternalPath } from '@/lib/url';

function sanitizeNext(next: string | null) {
  return sanitizeInternalPath(next);
}

function readAuthError() {
  const search = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  return search.get('error_description') ?? hash.get('error_description');
}

export default function AuthCallback() {
  const nav = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const next = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return sanitizeNext(params.get('next'));
  }, []);

  useEffect(() => {
    let alive = true;
    const timeout = window.setTimeout(() => {
      if (!alive) return;
      setError('Connexion incomplète. Réessaie depuis la page de connexion.');
    }, 2500);

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!alive || !session) return;
      window.clearTimeout(timeout);
      nav(next, { replace: true });
    });

    async function finishAuth() {
      const authError = readAuthError();
      if (authError) {
        window.clearTimeout(timeout);
        if (alive) setError(authError);
        return;
      }

      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      if (code) {
        const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
        if (exchangeError) {
          window.clearTimeout(timeout);
          if (alive) setError(exchangeError.message);
          return;
        }
      }

      const { data, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) {
        window.clearTimeout(timeout);
        if (alive) setError(sessionError.message);
        return;
      }

      if (data.session) {
        window.clearTimeout(timeout);
        nav(next, { replace: true });
      }
    }

    void finishAuth();

    return () => {
      alive = false;
      window.clearTimeout(timeout);
      sub.subscription.unsubscribe();
    };
  }, [nav, next]);

  return (
    <div className="min-h-screen flex flex-col justify-center px-6 bg-paper">
      <div className="max-w-sm w-full mx-auto text-center">
        <BrandLogo size={72} className="text-terre mx-auto mb-6" />
        <h1 className="t-ed-3xl">Connexion en cours.</h1>
        <div className="my-6 flex items-center gap-3">
          <GoldenRule />
          <span className="t-cap text-ink-3">auth</span>
          <GoldenRule />
        </div>

        {error ? (
          <div className="space-y-4">
            <p className="t-ui text-danger">{error}</p>
            <Button variant="primary" full onClick={() => nav('/login', { replace: true })}>
              Revenir à la connexion
            </Button>
          </div>
        ) : (
          <p className="t-ui text-ink-2">Nous finalisons votre session Supabase…</p>
        )}

        <p className="mt-8 t-ui-xs text-ink-3">
          Si rien ne se passe, <Link to="/login" className="underline">revenez à l’écran de connexion</Link>.
        </p>
      </div>
    </div>
  );
}
